import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import type { DealApi } from '@openx/types/deal';
import { useTranslation } from 'react-i18next';

import type { DealDetailsSectionProps } from '../../../types';
import { SupplementalAdQualityDetails } from '../../components/SupplementalAdQuality/SupplementalAdQualityDetails';
import { useDealContext } from '../../utils/context/DealContext';
import type { IUseOpenAuctionsFetch, IUseParticipantFunctions } from '../../utils/context/DealHookTypes';

import { ParticipantsDemandPartners, type ParticipantsDemandPartnersDeal } from './ParticipantsDemandPartners';

export type ParticipantDetailsSectionDeal = Pick<DealApi, 'open_auction_access' | 'deal_supplements'> &
  ParticipantsDemandPartnersDeal;

export function ParticipantsDetailsSection<T extends ParticipantDetailsSectionDeal>({
  deal,
  historyMode,
  useParticipantFunctions,
}: DealDetailsSectionProps<T> & { useParticipantFunctions: IUseParticipantFunctions }) {
  const { t } = useTranslation();

  const { useOpenAuctionsFetch } = useDealContext();

  const { open_auction_access } = deal;

  return (
    <FormSection data-test={'deal-form-participants'} divider historyMode={historyMode} title={t('Participants')}>
      {deal.deal_supplements && <SupplementalAdQualityDetails currentState={deal.deal_supplements} readonly />}
      <ParticipantsDemandPartners<T> deal={deal} useParticipantFunctions={useParticipantFunctions} />
      {useOpenAuctionsFetch && (
        <OpenAuctionsOptionsRender
          open_auction_access={open_auction_access}
          useOpenAuctionsFetch={useOpenAuctionsFetch}
        />
      )}
    </FormSection>
  );
}

const OpenAuctionsOptionsRender = ({
  open_auction_access,
  useOpenAuctionsFetch,
}: {
  open_auction_access: string | null;
  useOpenAuctionsFetch: IUseOpenAuctionsFetch;
}) => {
  const { t } = useTranslation();

  const { loading: optionsLoading, mapById: openAuctionOptionsMap } = useOpenAuctionsFetch();

  if (!open_auction_access) {
    return null;
  }

  return (
    <FieldDetails
      title={t('Set Open Auction Access for Participants')}
      loading={optionsLoading}
      value={openAuctionOptionsMap?.[open_auction_access]?.name}
    />
  );
};
